/*
================================================================================
File: src/pages/SellerLeads/EditLeadPage.js
Description: A detailed view for an existing seller lead, including all data points and actions.
================================================================================
Issues: Loan ID on AI query only pulls first letter.
Skip trace errors if no first owner and does not pull second owner.
*/
import React, { useState, useEffect, useCallback, useMemo } from "react";
import moment from "moment";
import instance from "../../api/axios";
import { views } from "../../constants/views";
import PageContent from "../../components/common/PageContent";
import Modal from "../../components/common/Modal";
import { formatMobileNumber } from "../../utils/formatters";
import {
  getAuctionOptionLabelClasses,
  getBuyHoldLabelClasses,
  getContractDeedLabelClasses,
  getEquityPartnerLabelClasses,
  getFixFlipLabelClasses,
  getFlipOnWrapLabelClasses,
  getHouseSwapLabelClasses,
  getLeaseOptionLabelClasses,
  getListingLabelClasses,
  getMortAssignLabelClasses,
  getShortSaleLabelClasses,
  getWholeSaleLabelClasses,
} from "../../constants/stopLightClasses";
import {
  options,
  offerDescriptions,
  boolOptions,
} from "../../constants/options";
import Select from "react-tailwindcss-select";
import { useAdaptiveMMKV } from "../../hooks/useAdaptiveMMKV";
import { storage } from "../../App";

// SVG Icons
const CalendarIcon = () => (<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>);
const MapPinIcon = () => (<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd"/></svg>);
const UserIcon = () => (<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/></svg>);
const ClockIcon = () => (<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>);
const ShieldIcon = () => (<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944A11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>);
const DollarIcon = () => (<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd"/></svg>);
const HomeIcon = () => (<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"/></svg>);
const DocumentIcon = () => (<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd"/></svg>);
const CreditCardIcon = () => (<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/><path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/></svg>);
const PenIcon = () => (<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"/></svg>);
const LinkIcon = () => (<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd"/></svg>);
const ImageIcon = () => (<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd"/></svg>);
const ChartIcon = () => (<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>);

// Helper component for displaying data rows consistently
const DetailRow = ({
  label,
  value,
  fieldName,
  onClick,
  isBool,
  isDate,
  isNumber,
  isTel,
}) => (
  <div
    className="flex items-center justify-between p-2 border-b-2 border-gray-200 dark:border-gray-700 last:border-b-0 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
    onClick={() =>
      onClick({ label, value, fieldName, isBool, isDate, isNumber, isTel })
    }
  >
    <span className="text-xs font-medium text-gray-500 dark:text-gray-200 uppercase">
      {label}:
    </span>
    <span className="text-sm font-medium text-gray-700 dark:text-gray-200 text-right dark:bg-gray-800">
      {value || "N/A"}
    </span>
  </div>
);

const AutoresponderModal = ({
  isOpen,
  onClose,
  lead,
  user,
  onSaveSuccess,
  autoresponders,
}) => {
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen && lead && autoresponders) {
      const currentSequence = autoresponders.find(
        (ar) => ar.sequence === lead.property_sequence,
      );
      setSelectedSequence(
        currentSequence
          ? {
              value: currentSequence.sequence,
              label: currentSequence.short_name,
            }
          : null,
      );
    }
  }, [isOpen, lead, autoresponders]);

  const handleSave = async () => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append("pid", lead.pid);
    formData.append("owner", user?.app_id);
    formData.append(
      "sequence",
      selectedSequence ? selectedSequence.value : "0",
    ); // Send '0' to remove

    try {
      await instance.post("/setAutoResponder.php", formData);
    } catch (error) {
      console.error("Error setting autoresponder:", error);
    } finally {
      setIsLoading(false);
      onClose();
      onSaveSuccess();
    }
  };

  const options = autoresponders
    ? autoresponders.map((ar) => ({ value: ar.sequence, label: ar.short_name }))
    : [];

  const footer = (
    <button
      onClick={handleSave}
      disabled={isLoading}
      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700"
    >
      {isLoading ? "Saving..." : "Set Autoresponder"}
    </button>
  );

  return (
    <Modal
      show={isOpen}
      onClose={onClose}
      title="Set Autoresponder"
      footer={footer}
      maxWidth="max-w-md"
      icon={
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
        </svg>
      }
      iconColor="blue"
    >
      <p className="mb-4">Select an autoresponder sequence for this lead.</p>
      <Select
        value={selectedSequence}
        onChange={(value) => setSelectedSequence(value)}
        options={[
          { value: "0", label: "None (Remove from Sequence)" },
          ...options,
        ]}
        isClearable={true}
        classNames={{
    menuButton: ({ isDisabled }) =>
      `flex text-sm text-gray-700 border border-gray-300 rounded shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-indigo-500 focus:ring focus:ring-indigo-500/20 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200 ${
        isDisabled ? "bg-gray-200" : ""
      }`,
    menu: "absolute z-10 w-full bg-white shadow-lg border rounded py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200",
    listItem: ({ isSelected }) =>
      `block transition duration-200 px-2 py-2 cursor-pointer select-none truncate rounded ${
        isSelected
          ? "text-white bg-indigo-500"
          : "text-gray-500 hover:bg-indigo-100 hover:text-indigo-500 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white"
      }`,
  }}
      />
    </Modal>
  );
};

// --- New Modal for Editing a Single Field ---
const EditDetailModal = ({
  isOpen,
  onClose,
  fieldData,
  onSave,
  user,
  setError,
  pullLeads,
}) => {
  const [currentValue, setCurrentValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (fieldData) {
      setCurrentValue(fieldData.value || "");
    }
  }, [fieldData]);

  const handleSave = async () => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append("pid", fieldData.pid);
    formData.append("owner", user?.app_id);
    formData.append("field", fieldData.fieldName);
    formData.append("value", currentValue);

    try {
      await instance.post("/updateLeadField.php", formData);
      setError(null);
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError("Error saving field: " + e.response?.data?.message || e.message);
        console.error("Error saving field: ", e.response?.data?.message || e.message);
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      await pullLeads(fieldData.pid);
      onSave(); // Trigger data refresh
      setIsLoading(false);
      onClose();
    }
  };

  if (!isOpen || !fieldData) return null;

  const footer = (
    <button
      onClick={handleSave}
      disabled={isLoading}
      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700"
    >
      {isLoading ? "Saving..." : "Save Changes"}
    </button>
  );

  return (
    <Modal
      show={isOpen}
      onClose={onClose}
      title={`Edit ${fieldData.label}`}
      footer={footer}
      maxWidth="max-w-md"
      icon={
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
        </svg>
      }
      iconColor="blue"
    >
      <label
        htmlFor="edit-value"
        className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-200"
      >
        {fieldData.label}
      </label>
      {fieldData.isTextArea ? (
        <textarea
          id="edit-value"
          value={currentValue}
          onChange={(e) => setCurrentValue(e.target.value)}
          className="w-full h-48 p-2 border border-gray-300 rounded-md dark:text-gray-200 dark:bg-gray-900"
        />
      ) : fieldData.isBool ? (
        <Select
          id="edit-value"
          value={{
            label: currentValue === "No" ? "No" : "Yes",
            value: currentValue === "No" ? "0" : "1",
            diabled: false,
          }}
          onChange={(selected) => {
            setCurrentValue(selected.value);
          }}
          options={boolOptions}
          classNames={{
    menuButton: ({ isDisabled }) =>
      `flex text-sm text-gray-700 border border-gray-300 rounded shadow-sm transition-all duration-300 focus:outline-none bg-white hover:border-gray-400 focus:border-indigo-500 focus:ring focus:ring-indigo-500/20 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200 ${
        isDisabled ? "bg-gray-200" : ""
      }`,
    menu: "absolute z-10 w-full bg-white shadow-lg border rounded py-1 mt-1.5 text-sm text-gray-700 dark:bg-gray-900 dark:border-gray-600 dark:text-gray-200",
    listItem: ({ isSelected }) =>
      `block transition duration-200 px-2 py-2 cursor-pointer select-none truncate rounded ${
        isSelected
          ? "text-white bg-indigo-500"
          : "text-gray-500 hover:bg-indigo-100 hover:text-indigo-500 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white"
      }`,
  }}
        />
      ) : (
        <input
          id="edit-value"
          type={
            fieldData.isDate
              ? "date"
              : fieldData.isNumber
                ? "number"
                : fieldData.isTel
                  ? "tel"
                  : "text"
          }
          value={fieldData.isNumber ? parseInt(currentValue.replace(/[^0-9.]+/g, "")) : currentValue}
          onChange={(e) => setCurrentValue(e.target.value)}
          className="w-full p-2 border border-gray-300 rounded-md dark:text-gray-200 dark:bg-gray-900"
        />
      )}
    </Modal>
  );
};

// Helper components for this page
const StoplightLabel = ({ label, value, className, onClick }) => (
  <button
    onClick={() => onClick(label)}
    className={`text-center p-2 m-1 rounded-md transition-transform transform hover:scale-90 ${className} dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700`}
  >
    <div className="text-sm uppercase dark:text-gray-200">{label}</div>
    <div className="text-sm dark:text-gray-200">
      {typeof value === "number"
        ? `$${value.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
        : value}
    </div>
  </button>
);

const TabButton = ({ tabName, children, activeTab, setActiveTab }) => {
  const isActive = activeTab === tabName;
  return (
    <button
      onClick={() => setActiveTab(tabName)}
      className={`px-6 py-3 font-semibold text-sm focus:outline-none ${
        isActive
          ? "border-b-2 border-blue-500 text-blue-600 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900"
          : "text-gray-500 hover:text-gray-700 "
      }`}
    >
      {children}
    </button>
  );
};

const CurrencyInput = ({ value, onChange, prefix = "$", suffix = ".00" }) => (
  <div className="relative">
    {prefix && (
      <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 dark:text-gray-200">
        {prefix}
      </span>
    )}
    <input
      type="num"
      className={`w-full p-2 border border-gray-300 rounded-md text-right focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-200 dark:bg-gray-900 ${
        prefix ? "pl-7" : "pl-3"
      } ${suffix ? "pr-12" : "pr-3"}`}
      value={value}
      onChange={onChange}
    />
    {suffix && (
      <span className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 dark:text-gray-200">
        {suffix}
      </span>
    )}
  </div>
);

// --- Modal 1: Price Value ---
const PriceValueModal = ({
  visible,
  onClose,
  lead,
  user,
  setError,
  pullLeads,
}) => {
  const [values, setValues] = useState({
    cma: lead.property_cma_value,
    attom: lead.zestimate,
    investor: lead.investor_price,
    appraisal: lead.property_assdTtlValue,
    seller: lead.seller_wants_price,
    zillow: lead.zestimate2,
  });
  const getInitialSelectedValue = (lead) => {
    // This checks each property in order of priority.
    // You can reorder these lines to change which value is selected by default if multiple are enabled.
    if (lead.property_cma_enabled === "1") return "cma";
    if (lead.property_inv_enabled === "1") return "investor";
    if (lead.property_ta_enabled === "1") return "appraisal";
    if (lead.property_seller_enabled === "1") return "seller";
    if (lead.property_zillow_enabled === "1") return "zillow";

    // Note: I've removed the ambiguous 'attom' condition since it used the same property as 'zillow'.
    // If 'attom' should be enabled by a different property, you can add its check here.
    // For example: if (lead.property_attom_enabled === "1") return 'attom';

    // Return a safe default value if no specific value is enabled.
    return "zillow";
  };

  const [selectedValue, setSelectedValue] = useState(() =>
    getInitialSelectedValue(lead),
  );

  const [isLoading, setIsLoading] = useState(false);

  const handleSave = async () => {
    setIsLoading(true);
    const formData = new FormData();

    // Append IDs and selected value
    formData.append("pid", lead.pid);
    formData.append("owner", user?.app_id);
    formData.append("selectedValue", selectedValue);

    // Append all price values
    for (const key in values) {
      formData.append(key, values[key]);
    }

    try {
      await instance.post("/savePriceValue.php", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setError(null);
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError("Error saving price values: " + e.response?.data?.message || e.message);
        console.error(" Error saving price values: ", e.response?.data?.message || e.message);
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }

      // Optionally, show an error message to the user
    } finally {
      await pullLeads(lead.pid);
      setIsLoading(false);
      onClose(); // Close modal on success
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg w-full max-w-lg dark:text-gray-200 dark:bg-gray-800 dark:border dark:border-gray-700">
        <ModalHeader title="Price Value" onClose={onClose} />
        <div className="p-6 space-y-4">
          {options.map((opt) => (
            <div key={opt.key} className="flex items-center justify-between">
              <label className="flex items-center flex-1 cursor-pointer">
                <input
                  type="radio"
                  name="priceValue"
                  checked={selectedValue === opt.key}
                  onChange={() => setSelectedValue(opt.key)}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <img
                  src={opt.logo}
                  alt={opt.label}
                  className="w-32 h-10 object-contain ml-4"
                />
              </label>
              <div className="w-40">
                <CurrencyInput
                  value={values[opt.key]}
                  onChange={(e) =>
                    setValues((v) => ({ ...v, [opt.key]: e.target.value }))
                  }
                />
              </div>
            </div>
          ))}
        </div>
        <ModalFooter onSave={handleSave} isLoading={isLoading} />
      </div>
    </div>
  );
};
const DebtCalculatorModal = ({ isOpen, onClose, initialData }) => {
  const [activeTab, setActiveTab] = useState("Mortgage 1");
  const [loanData, setLoanData] = useState({
    loan1: {
      originationDate: initialData?.loan1?.loan_1_origin || "2020-09-18",
      amount: initialData?.loan_1_loan_amount || 775000,
      term: initialData?.loan_1_loan_term || 30,
      rate: initialData?.loan_1_loan_intrest_rate || 2.25,
      backPay: initialData?.loan_1_back_pay || 0,
      taxLien: initialData?.other_lien_amounts || 0,
      taxEscrowedYearly: initialData?.tax_escrowed || 25622.16,
      insuranceEscrowedMonthly: initialData?.insur_escrowed || 535.76,
    },
    loan2: {
      originationDate: initialData?.loan2?.loan_2_origin || "",
      amount: initialData?.loan_2_loan_amount || 0,
      term: initialData?.loan_2_loan_term || 30,
      rate: initialData?.loan_2_loan_intrest_rate || 4.0,
      backPay: initialData?.loan_2_back_pay || 0,
    },
  });

  const [calculations, setCalculations] = useState({
    loan1: {},
    loan2: {},
  });

  useEffect(() => {
    const calculate = (loan) => {
      const principal = parseFloat(loan.amount) || 0;
      const annualRate = parseFloat(loan.rate) || 0;
      const termYears = parseInt(loan.term, 10) || 0;

      if (principal === 0 || annualRate === 0 || termYears === 0) {
        return { monthlyPI: 0, estimatedBalance: principal };
      }

      const monthlyRate = annualRate / 100 / 12;
      const numberOfPayments = termYears * 12;

      const monthlyPI =
        (principal *
          (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments))) /
        (Math.pow(1 + monthlyRate, numberOfPayments) - 1);

      const originationDate = new Date(loan.originationDate);
      const now = new Date();
      const monthsPassed =
        (now.getFullYear() - originationDate.getFullYear()) * 12 +
        (now.getMonth() - originationDate.getMonth());

      const estimatedBalance =
        (principal *
          (Math.pow(1 + monthlyRate, numberOfPayments) -
            Math.pow(1 + monthlyRate, monthsPassed))) /
        (Math.pow(1 + monthlyRate, numberOfPayments) - 1);

      const taxMonthly = (parseFloat(loan.taxEscrowedYearly) || 0) / 12;
      const insuranceMonthly = parseFloat(loan.insuranceEscrowedMonthly) || 0;
      const monthlyPITI = monthlyPI + taxMonthly + insuranceMonthly;

      return {
        monthlyPI,
        estimatedBalance,
        taxMonthly,
        monthlyPITI,
      };
    };

    setCalculations({
      loan1: calculate(loanData.loan1),
      loan2: calculate(loanData.loan2),
    });
  }, [loanData]);

  if (!isOpen) return null;

  const handleInputChange = (loanKey, field, value) => {
    setLoanData((prev) => ({
      ...prev,
      [loanKey]: {
        ...prev[loanKey],
        [field]: value,
      },
    }));
  };

  const totalOwed =
    (calculations.loan1.estimatedBalance || 0) +
    (calculations.loan2.estimatedBalance || 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 ">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl dark:text-gray-200 dark:bg-gray-800 dark:border dark:border-gray-700">
        {/* Header with Totals */}
        <div className="p-6 border-b">
          <div className="flex justify-between items-center text-lg mb-2">
            <span className="font-medium text-gray-600 dark:text-gray-200">Loan 1 Debt</span>
            <span className="font-semibold text-gray-800 dark:text-gray-200">
              $
              {(calculations.loan1.estimatedBalance || 0).toLocaleString(
                "en-US",
                { minimumFractionDigits: 2, maximumFractionDigits: 2 },
              )}
            </span>
          </div>
          <div className="flex justify-between items-center text-lg mb-3">
            <span className="font-medium text-gray-600 dark:text-gray-200">Loan 2 Debt</span>
            <span className="font-semibold text-gray-800 dark:text-gray-200 dark:text-gray-200">
              $
              {(calculations.loan2.estimatedBalance || 0).toLocaleString(
                "en-US",
                { minimumFractionDigits: 2, maximumFractionDigits: 2 },
              )}
            </span>
          </div>
          <div className="flex justify-between items-center text-xl border-t pt-3">
            <span className="font-bold text-gray-800 dark:text-gray-200">Total Amount Owed</span>
            <span className="font-bold text-blue-600 dark:text-gray-200">
              $
              {totalOwed.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <TabButton
            tabName="Mortgage 1"
            activeTab={activeTab}
            setActiveTab={() => setActiveTab("Mortgage 1")}
          >
            Mortgage 1
          </TabButton>
          <TabButton
            tabName="Mortgage 2"
            activeTab={activeTab}
            setActiveTab={() => setActiveTab("Mortgage 2")}
          >
            Mortgage 2
          </TabButton>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === "Mortgage 1" && (
            <LoanTabContent
              loanKey="loan1"
              data={loanData.loan1}
              calculations={calculations.loan1}
              onChange={handleInputChange}
            />
          )}
          {activeTab === "Mortgage 2" && (
            <LoanTabContent2
              loanKey="loan2"
              data={loanData.loan2}
              calculations={calculations.loan2}
              onChange={handleInputChange}
            />
          )}
        </div>

        <div className="p-4 bg-gray-50 flex justify-end rounded-b-lg dark:text-gray-200 dark:bg-gray-800">
          <button
            onClick={onClose}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const LoanTabContent2 = ({ loanKey, data, calculations, onChange }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
    {/* Left Column: Inputs */}
    <div className="space-y-4">
      <InputField
        label="Loan Origination Date"
        type="date"
        value={data.originationDate}
        onChange={(e) => onChange(loanKey, "originationDate", e.target.value)}
      />
      <InputField
        label="Loan Loan Amount"
        symbol="$"
        value={data.amount}
        onChange={(e) => onChange(loanKey, "amount", e.target.value)}
      />
      <InputField
        label="Loan Term"
        symbol="Year"
        isSuffix
        value={data.term}
        onChange={(e) => onChange(loanKey, "term", e.target.value)}
      />
      <InputField
        label="Loan Interest Rate (Default 4.0%)"
        symbol="%"
        isSuffix
        value={data.rate}
        onChange={(e) => onChange(loanKey, "rate", e.target.value)}
      />
      <InputField
        label="Loan Back Pay"
        symbol="$"
        value={data.backPay}
        onChange={(e) => onChange(loanKey, "backPay", e.target.value)}
      />
    </div>
    {/* Right Column: Calculations */}
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2 dark:text-gray-200">
        {loanKey.toUpperCase()} CALCULATIONS
      </h3>
      <CalculationRow
        label="Monthly Payment (P + I)"
        value={calculations.monthlyPI}
      />
      <CalculationRow
        label="Monthly Payment (PI&TI + Other)"
        value={calculations.monthlyPITI}
      />
      <CalculationRow
        label="Estimated Balance"
        value={calculations.estimatedBalance}
        isBold
      />
    </div>
  </div>
);

const LoanTabContent = ({ loanKey, data, calculations, onChange }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
    {/* Left Column: Inputs */}
    <div className="space-y-4">
      <InputField
        label="Loan Origination Date"
        type="date"
        value={data.originationDate}
        onChange={(e) => onChange(loanKey, "originationDate", e.target.value)}
      />
      <InputField
        label="Loan Loan Amount"
        symbol="$"
        value={data.amount}
        onChange={(e) => onChange(loanKey, "amount", e.target.value)}
      />
      <InputField
        label="Loan Term"
        symbol="Year"
        isSuffix
        value={data.term}
        onChange={(e) => onChange(loanKey, "term", e.target.value)}
      />
      <InputField
        label="Loan Interest Rate (Default 4.0%)"
        symbol="%"
        isSuffix
        value={data.rate}
        onChange={(e) => onChange(loanKey, "rate", e.target.value)}
      />
      <InputField
        label="Loan Back Pay"
        symbol="$"
        value={data.backPay}
        onChange={(e) => onChange(loanKey, "backPay", e.target.value)}
      />
      <InputField
        label="Tax Lien(s) Amount"
        symbol="$"
        value={data.taxLien}
        onChange={(e) => onChange(loanKey, "taxLien", e.target.value)}
      />
    </div>
    {/* Right Column: Calculations */}
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2 dark:text-gray-200">
        {loanKey.toUpperCase()} CALCULATIONS
      </h3>
      <CalculationRow
        label="Monthly Payment (P + I)"
        value={calculations.monthlyPI}
      />
      <CalculationRow
        label="Monthly Payment (PI&TI + Other)"
        value={calculations.monthlyPITI}
      />
      <CalculationRow
        label="Estimated Balance"
        value={calculations.estimatedBalance}
        isBold
      />
      <CalculationRow
        label="Tax Escrowed Monthly"
        value={calculations.taxMonthly}
      />
      <CalculationRow
        label="Tax Escrowed Yearly"
        value={data.taxEscrowedYearly}
      />
      <CalculationRow
        label="Insurance Escrowed Monthly"
        value={data.insuranceEscrowedMonthly}
      />
    </div>
  </div>
);

const InputField = ({
  label,
  symbol,
  value,
  onChange,
  type = "number",
  isSuffix = false,
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
      {label}
    </label>
    <div className="relative">
      {!isSuffix && (
        <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500 dark:text-gray-200">
          {symbol}
        </span>
      )}
      <input
        type={type}
        value={value}
        onChange={onChange}
        className={`w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-200 dark:bg-gray-900 ${
          !isSuffix ? "pl-7 pr-12" : "pr-16"
        }`}
      />
      <span
        className={`absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 dark:text-gray-200 ${
          isSuffix ? "" : "pr-3"
        }`}
      >
        {isSuffix ? symbol : type === "date" ? "" : ".00"}
      </span>
    </div>
  </div>
);

const ModalHeader = ({ title, onClose }) => (
  <div className="flex justify-between items-center p-4 border-b border-gray-200">
    <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">{title}</h3>
    <button
      onClick={onClose}
      className="text-gray-400 hover:text-gray-600 text-3xl font-light "
    >
      &times;
    </button>
  </div>
);

const ModalFooter = ({ onSave, isLoading }) => (
  <div className="p-4 bg-gray-50 flex justify-end rounded-b-lg dark:text-gray-200 dark:bg-gray-800">
    <button
      onClick={onSave}
      disabled={isLoading}
      className="bg-blue-600 text-white font-semibold px-6 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:bg-blue-300 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700"
    >
      {isLoading ? "Saving..." : "Save"}
    </button>
  </div>
);

const PhotoManagerModal = ({
  isOpen,
  onClose,
  existingPhotos,
  user,
  pid,
  pullData,
  setError,
  pullLeads,
}) => {
  const [photos, setPhotos] = useState([]);
  const [newPhotoFiles, setNewPhotoFiles] = useState([]);
  const [newPhotoPreviews, setNewPhotoPreviews] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setisLoading] = useState(false);

  const handleFileChange = (files) => {
    if (files && files.length > 0) {
      const fileList = Array.from(files);
      setNewPhotoFiles((prev) => [...prev, ...fileList]);

      const previewUrls = fileList.map((file) => URL.createObjectURL(file));
      setNewPhotoPreviews((prev) => [...prev, ...previewUrls]);
    }
  };

  useEffect(() => {
    setPhotos(existingPhotos);
  }, [existingPhotos]); // The empty dependency array ensures this runs only on mount.

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFileChange(e.dataTransfer.files);
  };

  const removeExistingPhoto = (idToRemove) => {
    setPhotos(photos.filter((p) => p.id !== idToRemove));
  };

  const removeNewPhoto = (indexToRemove) => {
    setNewPhotoFiles(
      newPhotoFiles.filter((_, index) => index !== indexToRemove),
    );
    setNewPhotoPreviews(
      newPhotoPreviews.filter((_, index) => index !== indexToRemove),
    );
  };

  const handleSave = async () => {
    setisLoading(true);
    console.log("Existing photos remaining:", photos);
    console.log("New photos to upload:", newPhotoFiles);
    // Here you would typically handle the upload logic for newPhotoFiles
    // and send a request to delete photos that were removed.
    const formData = new FormData();

    // Add the IDs and owner info
    formData.append("pid", pid); // Example Property ID
    formData.append("owner", user?.app_id); // Example Owner ID

    // Add the list of photos to keep as a JSON string
    formData.append("existingPhotos", JSON.stringify(photos));

    // Append each new file to the FormData object
    newPhotoFiles.forEach((file) => {
      formData.append(`newPhotos[]`, file, file.name);
    });
    try {
      // Send the data to your PHP script
      await instance.post("/updateImages.php", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // On success, update the local state and close the modal
      // In a real app, you might refetch the photos list here

      setError(null);
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError("Error uploading photo: " + e.response?.data?.message || e.message);
        console.error("Error uploading photo: ", e.response?.data?.message || e.message);
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }

      // Handle error (e.g., show a notification to the user)
    } finally {
      onClose();
      setisLoading(false);
      setPhotos(photos);
      await pullData(user?.app_id);
      await pullLeads(pid);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col dark:text-gray-200 dark:bg-gray-800 dark:border dark:border-gray-700">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 dark:bg-gray-800">Manage Photos</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-3xl "
          >
            &times;
          </button>
        </div>

        <div className="p-6 overflow-y-auto dark:text-gray-200 dark:bg-gray-800">
          {/* Drag and Drop Area */}
          <div className="flex items-center justify-center w-full mb-6 dark:text-gray-200 dark:bg-gray-800">
            <label
              htmlFor="dropzone-file"
              className={`flex flex-col items-center justify-center w-full h-64 border-2 border-dashed rounded-lg cursor-pointer transition-colors  dark:text-gray-200 dark:bg-gray-900 ${
                isDragging
                  ? "border-blue-500 bg-blue-50 dark:bg-gray-900"
                  : "border-gray-300 bg-gray-50 hover:bg-gray-100 dark:hover:bg-gray-900"
              }`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6 dark:text-gray-200 dark:bg-gray-900">
                <svg
                  className="w-8 h-8 mb-4 text-gray-500"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 20 16"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                  />
                </svg>
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">Click to upload</span> or drag
                  and drop
                </p>
                <p className="text-xs text-gray-500">PNG, JPG</p>
              </div>
              <input
                id="dropzone-file"
                type="file"
                className="hidden"
                multiple
                onChange={(e) => handleFileChange(e.target.files)}
              />
            </label>
          </div>

          {/* Photo Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 dark:text-gray-200 dark:bg-gray-800">
            {/* Existing Photos */}
            {photos.map((photo) => (
              <div key={photo.id} className="relative group">
                <img
                  src={
                    "https://app.bigdogtools.com/sites/image.php?v=v2&scale=1.0&dir=m&filename=" +
                    photo.url
                  }
                  alt="Existing content"
                  className="w-full h-32 object-cover rounded-lg"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity flex items-center justify-center">
                  <button
                    onClick={() => removeExistingPhoto(photo.id)}
                    className="text-white opacity-0 group-hover:opacity-100 transition-opacity bg-red-600 rounded-full p-2 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      ></path>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
            {/* New Photo Previews */}
            {newPhotoPreviews.map((preview, index) => (
              <div key={index} className="relative group">
                <img
                  src={preview}
                  alt={`New upload ${index + 1}`}
                  className="w-full h-32 object-cover rounded-lg"
                />
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity flex items-center justify-center">
                  <button
                    onClick={() => removeNewPhoto(index)}
                    className="text-white opacity-0 group-hover:opacity-100 transition-opacity bg-red-600 rounded-full p-2 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      ></path>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-4 bg-gray-50 flex justify-end rounded-b-lg space-x-2 dark:text-gray-200 dark:bg-gray-800">
          {isLoading ? (
            <div className="flex justify-center items-center h-6 mt-2">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500 "></div>
              <span className="ml-4 text-blue-500 text-lg">Saving...</span>
            </div>
          ) : (
            <button
              onClick={handleSave}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700"
            >
              Save
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const CalculationRow = ({ label, value, isBold = false }) => (
  <div
    className={`flex justify-between items-center ${isBold ? "font-bold" : ""}`}
  >
    <span className="text-gray-600 dark:text-gray-200">{label}</span>
    <span className="text-gray-800 dark:text-gray-200">
      $
      {(value || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}
    </span>
  </div>
);

// --- Modal 2: Seller Debt ---
const SellerDebtModal = ({
  visible,
  onClose,
  lead,
  user,
  setError,
  pullLeads,
}) => {
  const [loan1, setLoan1] = useState("0");
  const [loan2, setLoan2] = useState("0");
  const [liens, setLiens] = useState("0");
  const [totalDebt, setTotalDebt] = useState("0");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (lead) {
      setLoan1(lead.loan_1_loan_balance || "0");
      setLoan2(lead.loan_2_loan_balance || "0");
      setLiens(lead.other_lien_amounts || "0");
    }
  }, [lead]);

  useEffect(() => {
    const total =
      (parseFloat(loan1) || 0) +
      (parseFloat(loan2) || 0) +
      (parseFloat(liens) || 0);
    setTotalDebt(total.toFixed(2));
  }, [loan1, loan2, liens]);

  const handleSave = async () => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append("pid", lead.pid);
    formData.append("owner", user?.app_id);
    formData.append("loan_1_loan_balance", loan1);
    formData.append("loan_2_loan_balance", loan2);
    formData.append("other_lien_amounts", liens);

    try {
      await instance.post("/updateSellerDebt.php", formData);
      setError(null);
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError("Error updating debt: " + e.response?.data?.message || e.message);
        console.error("Error updating debt: ", e.response?.data?.message || e.message);
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      await pullLeads(lead.pid);
      setIsLoading(false);
      onClose();
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg w-full max-w-sm dark:border dark:border-gray-700 dark:text-gray-200 dark:bg-gray-800 ">
        <ModalHeader title="Seller Debt" onClose={onClose} />
        <div className="p-6 space-y-4">
          <div className="flex justify-between items-center">
            <label className="font-medium text-gray-700 dark:text-gray-200">
              Mortgage 1 Estimated:
            </label>
            <div className="w-36">
              <CurrencyInput
                value={loan1}
                onChange={(e) => setLoan1(e.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-between items-center">
            <label className="font-medium text-gray-700 dark:text-gray-200">
              Mortgage 2 Estimated:
            </label>
            <div className="w-36">
              <CurrencyInput
                value={loan2}
                onChange={(e) => setLoan2(e.target.value)}
              />
            </div>
          </div>
          <div className="flex justify-between items-center">
            <label className="font-medium text-gray-700 dark:text-gray-200">
              All Liens Estimated:
            </label>
            <div className="w-36">
              <CurrencyInput
                value={liens}
                onChange={(e) => setLiens(e.target.value)}
              />
            </div>
          </div>
          <div className="border-t border-gray-200 pt-4 mt-2 flex justify-between items-center">
            <label className="font-bold text-gray-800">Estimated Debt:</label>
            <div className="w-48">
              <CurrencyInput value={totalDebt} onChange={() => {}} />
            </div>
          </div>
        </div>
        <ModalFooter onSave={handleSave} isLoading={isLoading} />
      </div>
    </div>
  );
};

// --- Modal 3: Repairs ---
const RepairsModal = ({
  visible,
  onClose,
  lead,
  user,
  setError,
  pullLeads,
}) => {
  const [values, setValues] = useState({
    estimated: "0",
    contractor: "0",
    manual: "0",
  });
  const getInitialSelectedValue = (lead) => {
    // This checks each property in order of priority.
    // You can reorder these lines to change which value is selected by default if multiple are enabled.
    if (lead.property_apprep_enabled === "1") return "estimated";
    if (lead.property_bidrep_enabled === "1") return "contractor";
    if (lead.property_manrep_enabled === "1") return "manual";

    // Note: I've removed the ambiguous 'attom' condition since it used the same property as 'zillow'.
    // If 'attom' should be enabled by a different property, you can add its check here.
    // For example: if (lead.property_attom_enabled === "1") return 'attom';

    // Return a safe default value if no specific value is enabled.
    return "manual";
  };

  // Use the helper function to initialize the state.
  const [selectedValue, setSelectedValue] = useState(() =>
    getInitialSelectedValue(lead),
  );

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (lead) {
      setValues({
        estimated: lead.repairs_estimated || "0",
        contractor: lead.repairs_contractor || "0",
        manual: lead.repairs_manual || lead.property_repair_costs || "0",
      });
      // This logic would need to be enhanced if you store which value was last selected
      setSelectedValue("manual");
    }
  }, [lead]);

  const options = [
    { key: "estimated", label: "Estimated Repair Value:" },
    { key: "contractor", label: "Contractor Repair Value:" },
    { key: "manual", label: "Manual Repair Value:" },
  ];

  const handleSave = async () => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append("pid", lead.pid);
    formData.append("owner", user?.app_id);
    formData.append("estimated", values.estimated);
    formData.append("contractor", values.contractor);
    formData.append("manual", values.manual);
    formData.append("selectedValue", selectedValue);

    try {
      await instance.post("/updateRepairs.php", formData);
      setError(null);
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError("Error updating repairs: " + e.response?.data?.message || e.message);
        console.error("Error updating repairs: ", e.response?.data?.message || e.message);
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
      setIsLoading(false);
      await pullLeads(lead.pid);
      onClose();
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg w-full max-w-md dark:border dark:border-gray-700 dark:text-gray-200 dark:bg-gray-800">
        <ModalHeader title="Repairs" onClose={onClose} />
        <div className="p-6 space-y-4">
          {options.map((opt) => (
            <div key={opt.key} className="flex items-center justify-between">
              <label className="flex items-center cursor-pointer ">
                <input
                  type="radio"
                  name="repairValue"
                  checked={selectedValue === opt.key}
                  onChange={() => setSelectedValue(opt.key)}
                  className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="ml-3 font-medium text-gray-700 dark:text-gray-200">
                  {opt.label}
                </span>
              </label>
              <div className="w-36">
                <CurrencyInput
                  value={values[opt.key]}
                  onChange={(e) =>
                    setValues((v) => ({ ...v, [opt.key]: e.target.value }))
                  }
                />
              </div>
            </div>
          ))}
        </div>
        <ModalFooter onSave={handleSave} isLoading={isLoading} />
      </div>
    </div>
  );
};

const DescriptionModal = ({ isOpen, onClose, title, description }) => {
  if (!isOpen) return null;
  return (
    <Modal show={isOpen} onClose={onClose} title={title} icon={
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H7a1 1 0 01-1-1v-6z" clipRule="evenodd" />
        </svg>
      }
      iconColor="blue"
    >
      <div>
        {description.split("\n").map((line, index) => (
          <p key={index} className="text-gray-600 mb-2 last:mb-0 dark:text-gray-200">
            {line}
          </p>
        ))}
      </div>
    </Modal>
  );
};

const EditLeadPage = ({ onViewChange, leads, user, pulldata }) => {
  const [lead, setLead] = useState(leads);
  const [thumbnails, setThumbnails] = useState([]);
  const [mainImage, setMainImage] = useState({
    id: "0",
    pid: leads.pid,
    url: leads.image,
  });
  const [error, setError] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [fieldToEdit, setFieldToEdit] = useState(null);
  const [modalImage, setModalImage] = useState("");
  const [activeModal, setActiveModal] = useState(null);
  const [isModalMCOpen, setIsModalMCOpen] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: "",
    description: "",
  });
  const [helpOpen, sethelpOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isAiConfirmOpen, setIsAiConfirmOpen] = useState(false);
  const [isSkipConfirmOpen, setIsSkipConfirmOpen] = useState(false);
  const [loading, setLoading] = useState({
    ai: false,
    skip: false,
    skip2: false,
    delete: false,
  });
  const [isAutoresponderModalOpen, setIsAutoresponderModalOpen] =
    useState(false);
  const [isToolsDropdownOpen, setIsToolsDropdownOpen] = useState(false);
  const [sellerAR] = useAdaptiveMMKV("user.Feed.Seller.Responders", storage);

  const onSaveSuccess = async () => {
    await pullLeads(lead.pid);
  };

  const pullLeads = useCallback(async (pid) => {
    try {
      const response = await instance.get(`/LoadHomes.php?u=${pid}&type=view`);
      setLead(response.data[0]);
      setError(null);
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError("Error pulling lead details: " + e.response?.data?.message || e.message);
        console.error("Error pulling lead details: ", e.response?.data?.message || e.message);
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
    }
  }, []);

  const pullAllImages = useCallback(async () => {
    if (!lead.pid) return;
    try {
      const response = await instance.get(
        `/LoadImages.php?u=${lead.pid}&type=viewurl`,
      );
      setThumbnails(response.data);
      setError(null);
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError("Error fetching images: " + e.response?.data?.message || e.message);
        console.error("Error fetching images: ", e.response?.data?.message || e.message);
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    } finally {
    }
  }, [lead.pid]);

  useEffect(() => {
    if (leads && leads.pid) {
      pullAllImages();
    } else {
      onViewChange(views.seller, "sellerleads");
    }
  }, [leads, onViewChange, pullAllImages]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const toolsDropdown = document.querySelector('[data-tools-dropdown]');
      if (toolsDropdown && !toolsDropdown.contains(event.target)) {
        setIsToolsDropdownOpen(false);
      }
    };

    if (isToolsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isToolsDropdownOpen]);

  const handleAction = async (action) => {
    setLoading((prev) => ({ ...prev, [action]: true }));
    const formData = new FormData();
    formData.append("pid", lead.pid);
    formData.append("owner", user?.app_id);

    let url = "";
    switch (action) {
      case "ai":
        formData.append("prop_address", lead.property_address);
        formData.append("prop_city", lead.property_city);
        formData.append("prop_state", lead.property_state);

        url = "/aiQuery.php";
        break;
      case "skip":
        formData.append("option", "primary");
        url = "/leadSkipTrace.php";
        break;
      case "skip2":
        formData.append("option", "secondary");
        url = "/leadSkipTrace.php";
        break;
      case "delete":
        formData.append("id", lead.pid);
        url = "/deleteHome.php";
        break;
      case "contract":
        formData.append("action", "download");
        formData.append("id", lead.pid);
        url = "/";
        break;
      case "analysis":
        formData.append("action", "download");
        formData.append("id", lead.pid);
        url = "/contract_Analysis.php";
        break;
      default:
        setLoading((prev) => ({ ...prev, [action]: false }));
        return;
    }
    try {
      const response = await instance.post(url, formData);
      if (action === "delete") {
        onViewChange("sellerleads", "sellerleads");
      } else if (action === "ai") {
        setIsAiConfirmOpen(false);
      } else if (action === "skip" || action === "skip2") {
        setIsSkipConfirmOpen(false);
      } else if (action === "analysis") {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Contract_Analysis.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      setError(null);
    } catch (e) {
      if (e.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        setError(e.response?.data?.message || e.message);
        console.error(`Error with action ${action}: `, e.response?.data?.message || e.message);
      } else if (e.request) {
        // The request was made but no response was received
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
      setError(e.response?.data?.message || e.message);
      console.error(`Error with action ${action}:` + e.response?.data?.message || e.message);
    } finally {
      setLoading((prev) => ({ ...prev, [action]: false }));
      await pullLeads(lead.pid);
      await pulldata(user?.app_id);
    }
    setIsAiConfirmOpen(false);
    setIsSkipConfirmOpen(false);
  };
  const handleDetailClick = (fieldData) => {
    setFieldToEdit({ ...fieldData, pid: lead.pid });
    setIsEditModalOpen(true);
  };

  const handleLabelClick = (title) => {
    setModalContent({
      title,
      description: offerDescriptions[title] || "No description available.",
    });
    setIsDescriptionModalOpen(true);
  };

  const handleThumbnailClick = (clickedImage) => setMainImage(clickedImage);
  const openImageModal = () => setIsImageModalOpen(true);

  const {
    ARV,
    repairs,
    debt,
    asisValue,
    EQP,
    loyCal,
    needHome,
    isCommercial,
    Lo1C,
    listingIsNotGreen,
    needsale,
    listed,
    valueNetProceedsRetail,
    state,
  } = useMemo(() => {
    const ARV = parseFloat(lead.property_valued_price) || 0;
    const repairs = parseFloat(lead.property_repair_costs) || 0;
    const debt = parseFloat(lead.total_debt) || 0;
    const asisValue = ARV - repairs;
    const equity = asisValue - debt;
    const EQP = ARV > 0 ? equity / ARV : 0;
    const loyCal = Math.max(debt, 0.9 * ARV);
    const needHome = lead.needs_home;
    const commercial = lead.property_type || "0";
    const today = new Date();
    const date2 = lead.property_state === "" ? today : lead.loan_1_last_pay;
    const days = Math.floor((date2 - today) / (1000 * 60 * 60 * 24));
    const Lo1C = days < 35;
    const listingIsNotGreen = false; // Mocking this value from PHP
    const needsale = lead.need_to_sell_days || "0";
    const listed = lead.property_listed || "0";
    const isCommercial = commercial === "Commercial";
    const state = lead.property_state || "TX";
    const valueNetProceedsRetail = equity;
    return {
      ARV,
      repairs,
      debt,
      asisValue,
      equity,
      EQP,
      loyCal,
      needHome,
      isCommercial,
      Lo1C,
      listingIsNotGreen,
      needsale,
      listed,
      valueNetProceedsRetail,
      state,
    };
  }, [lead]);

  return (
    <PageContent
      title="Seller Leads"
      subtitle="All Leads"
      thirdtitle={leads.property_address}
      error={error}
    >
      {/* Hero Banner with Lead Details */}
      <div className="mx-8 mt-8 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-10 rounded-xl shadow-lg mb-8">
        <div className="flex items-start justify-between flex-wrap gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-4 mb-2">
              <div className="p-3 bg-white/20 rounded-lg">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10.5 1.5H3.75A2.25 2.25 0 001.5 3.75v12.5A2.25 2.25 0 003.75 18.5h12.5a2.25 2.25 0 002.25-2.25V9.5m-15-4h9m-9 4h9m-9 4h6M10 1.5v4m4.5-4v4" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl font-bold">{leads.property_address}</h1>
                <p className="text-orange-100 text-sm mt-1">{leads.property_city}, {leads.property_state} {leads.property_zip}</p>
              </div>
            </div>
          </div>
          <div className="text-right text-orange-100 text-sm">
            <div className="font-semibold">Property ID: {leads.pid}</div>
            <div>Listed: {moment(leads.lead_date).format("MMM D, YYYY")}</div>
          </div>
        </div>
      </div>

      <div className="px-8 pb-8">
        {/* Primary Action Buttons */}
        <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
          <div className="flex gap-3">
            <button
              onClick={() => onViewChange("sellerleads", "sellerleads")}
              className="px-5 py-2 text-sm font-medium rounded-lg text-white bg-orange-600 hover:bg-orange-700 dark:bg-orange-700 dark:hover:bg-orange-600 transition-colors flex items-center gap-2"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 1024 1024"
                fill="#fff"
                className="icon"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M669.6 849.6c8.8 8 22.4 7.2 30.4-1.6s7.2-22.4-1.6-30.4l-309.6-280c-8-7.2-8-17.6 0-24.8l309.6-270.4c8.8-8 9.6-21.6 2.4-30.4-8-8.8-21.6-9.6-30.4-2.4L360.8 480.8c-27.2 24-28 64-0.8 88.8l309.6 280z"
                  fill=""
                />
              </svg>
              Back to Leads
            </button>
            <button
              onClick={() => sethelpOpen(true)}
              className="px-4 py-2 text-sm font-medium rounded-lg text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 16 16"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M8.568 1.031A6.8 6.8 0 0 1 12.76 3.05a7.06 7.06 0 0 1 .46 9.39 6.85 6.85 0 0 1-8.58 1.74 7 7 0 0 1-3.12-3.5 7.12 7.12 0 0 1-.23-4.71 7 7 0 0 1 2.77-3.79 6.8 6.8 0 0 1 4.508-1.149zM9.04 13.88a5.89 5.89 0 0 0 3.41-2.07 6.07 6.07 0 0 0-.4-8.06 5.82 5.82 0 0 0-7.43-.74 6.06 6.06 0 0 0 .5 10.29 5.81 5.81 0 0 0 3.92.58zM7.375 6h1.25V5h-1.25v1zm1.25 1v4h-1.25V7h1.25z"
                />
              </svg>{" "}
              <span>How to Edit</span>
            </button>
          </div>
          <button
            onClick={() => setIsDeleteConfirmOpen(true)}
            disabled={loading.delete}
            className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 transition-colors duration-200 flex items-center justify-center space-x-2 dark:text-gray-200 dark:bg-red-700 dark:hover:bg-red-600"
          >
            <svg
              fill="currentColor"
              width="24px"
              height="24px"
              viewBox="-3.5 0 19 19"
              xmlns="http://www.w3.org/2000/svg"
              className="cf-icon-svg"
            >
              <path d="M11.383 13.644A1.03 1.03 0 0 1 9.928 15.1L6 11.172 2.072 15.1a1.03 1.03 0 1 1-1.455-1.456l3.928-3.928L.617 5.79a1.03 1.03 0 1 1 1.455-1.456L6 8.261l3.928-3.928a1.03 1.03 0 0 1 1.455 1.456L7.455 9.716z" />
            </svg>
            <span>{loading.delete ? "Deleting..." : "Delete Lead"}</span>
          </button>
        </div>
        {/* Action Buttons - Cleaner Layout */}
        <div className="flex flex-wrap gap-3 mb-6 items-center justify-center md:justify-start">
          {/* Core Actions */}
          <button
            onClick={() => setModalImage(true)}
            className="px-4 py-2 text-sm font-medium rounded-lg text-gray-800 bg-cyan-200 hover:bg-cyan-300 transition-colors duration-200 flex items-center space-x-2 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M15.5 10a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z" fill="currentColor" />
              <path d="M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2-2H5a2 2 0 0 1-2-2V5zm16 0H5v7.92l3.375-2.7a1 1 0 0 1 1.25 0l4.3 3.44 1.368-1.367a1 1 0 0 1 1.414 0L19 14.586V5zM5 19h14v-1.586l-3-3-1.293 1.293a1 1 0 0 1-1.332.074L9 12.28l-4 3.2V19z" fill="currentColor" />
            </svg>
            <span>Photos</span>
          </button>

          <button
            onClick={() => setIsAiConfirmOpen(true)}
            disabled={loading.ai}
            className="px-4 py-2 text-sm font-medium rounded-lg text-gray-800 bg-pink-200 hover:bg-pink-300 transition-colors duration-200 flex items-center space-x-2 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="miter">
              <rect x="9" y="9" width="6" height="6" fill="currentColor" opacity="0.1" rx="0"></rect>
              <rect x="9" y="9" width="6" height="6" fill="currentColor" rx="0"></rect>
              <rect x="5" y="5" width="14" height="14" rx="0"></rect>
            </svg>
            <span>{loading.ai ? "Querying..." : "AI Query"}</span>
          </button>

          <button
            onClick={() => setIsSkipConfirmOpen(true)}
            disabled={loading.skip}
            className="px-4 py-2 text-sm font-medium rounded-lg text-gray-800 bg-sky-200 hover:bg-sky-300 transition-colors duration-200 flex items-center space-x-2 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 4a6 6 0 1 0 0 12 6 6 0 0 0 0-12zm-8 6a8 8 0 1 1 14.32 4.906l5.387 5.387a1 1 0 0 1-1.414 1.414l-5.387-5.387A8 8 0 0 1 2 10z" fill="currentColor" />
            </svg>
            <span>{loading.skip || loading.skip2 ? "Skipping..." : "Skiptrace"}</span>
          </button>

          <button
            onClick={() => onViewChange("contractform", "sellerleads")}
            className="px-4 py-2 text-sm font-medium rounded-lg text-gray-800 bg-amber-200 hover:bg-amber-300 transition-colors duration-200 flex items-center space-x-2 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700"
          >
            <svg width="20" height="20" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4.5 7H4V8H4.5V7ZM10.5 8H11V7H10.5V8ZM4.5 4H4V5H4.5V4ZM6.5 5H7V4H6.5V5ZM10.5 0.5L10.8536 0.146447L10.7071 0H10.5V0.5ZM13.5 3.5H14V3.29289L13.8536 3.14645L13.5 3.5ZM8 11L7.64645 11.3536L8 11ZM4.5 8H10.5V7H4.5V8ZM4.5 5H6.5V4H4.5V5ZM12.5 14H2.5V15H12.5V14ZM2 13.5V1.5H1V13.5H2ZM2.5 1H10.5V0H2.5V1ZM13 3.5V13.5H14V3.5H13ZM10.1464 0.853553L13.1464 3.85355L13.8536 3.14645L10.8536 0.146447L10.1464 0.853553ZM2.5 14C2.22386 14 2 13.7761 2 13.5H1C1 14.3284 1.67157 15 2.5 15V14ZM12.5 15C13.3284 15 14 14.3284 14 13.5H13C13 13.7761 12.7761 14 12.5 14V15ZM2 1.5C2 1.22386 2.22386 1 2.5 1V0C1.67157 0 1 0.671574 1 1.5H2Z" fill="currentColor" />
            </svg>
            <span>Contract</span>
          </button>

          {/* Tools Dropdown */}
          <div className="relative" data-tools-dropdown>
            <button
              onClick={() => setIsToolsDropdownOpen(!isToolsDropdownOpen)}
              className="px-4 py-2 text-sm font-medium rounded-lg text-gray-800 bg-gray-200 hover:bg-gray-300 transition-colors duration-200 flex items-center space-x-2 dark:text-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 dark:border dark:border-gray-600"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="1"></circle>
                <circle cx="19" cy="12" r="1"></circle>
                <circle cx="5" cy="12" r="1"></circle>
              </svg>
              <span>More Tools</span>
            </button>

            {/* Dropdown Menu */}
            {isToolsDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                <button
                  onClick={() => {
                    setIsModalMCOpen(true);
                    setIsToolsDropdownOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 rounded-t-lg"
                >
                  <svg width="18" height="18" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4.5H11M4 8.5H5M7 8.5H8M10 8.5H11M4 11.5H5M7 11.5H8M10 11.5H11M2.5 14.5H12.5C13.0523 14.5 13.5 14.0523 13.5 13.5V1.5C13.5 0.947715 13.0523 0.5 12.5 0.5H2.5C1.94772 0.5 1.5 0.947716 1.5 1.5V13.5C1.5 14.0523 1.94772 14.5 2.5 14.5Z" stroke="currentColor" strokeWidth="1" />
                  </svg>
                  <span>Mortgage Calculator</span>
                </button>
                <button
                  onClick={() => {
                    setIsAutoresponderModalOpen(true);
                    setIsToolsDropdownOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                >
                  <svg fill="currentColor" width="18" height="18" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                    <g data-name="Layer 21" id="Layer_21">
                      <path d="M27.61,7h-2a1,1,0,0,1,.95,1.32l-5.33,16a1,1,0,0,1-.95.68h2a1,1,0,0,0,.95-.68l5.33-16A1,1,0,0,0,27.61,7Z" />
                      <path d="M13,15a1,1,0,0,1-.5-.13l-7-4a1,1,0,0,1,1-1.74l6.59,3.77L23.66,9.06a1,1,0,0,1,.68,1.88l-11,4A1,1,0,0,1,13,15Z" />
                      <path d="M22.28,26H5a1,1,0,0,1,0-2H22.28L27.61,8H3A1,1,0,0,1,3,6H27.61a2,2,0,0,1,1.9,2.63l-5.33,16A2,2,0,0,1,22.28,26Z" />
                    </g>
                  </svg>
                  <span>Autoresponder</span>
                </button>
                <button
                  onClick={() => {
                    handleAction("analysis");
                    setIsToolsDropdownOpen(false);
                  }}
                  disabled={loading.analysis}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 rounded-b-lg disabled:opacity-50"
                >
                  <svg fill="currentColor" width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" enableBackground="new 0 0 24 24">
                    <path d="M21.7,7.3c-0.4-0.4-1-0.4-1.4,0L14,13.6L9.7,9.3C9.5,9.1,9.3,9,9,9C8.7,9,8.5,9.1,8.3,9.3l-6,6C2.1,15.5,2,15.7,2,16c0,0.6,0.4,1,1,1c0.3,0,0.5-0.1,0.7-0.3L9,11.4l4.3,4.3c0.1,0.1,0.2,0.2,0.3,0.2C13.7,16,13.9,16,14,16c0.2,0,0.5-0.1,0.6-0.3c0,0,0,0,0.1,0c0,0,0,0,0,0s0,0,0,0l7-7C22.1,8.3,22.1,7.7,21.7,7.3z" />
                  </svg>
                  <span>{loading.analysis ? "Generating..." : "Lead Analysis"}</span>
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400">
                  <ImageIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Property Photos</h3>
              </div>
              <div className="flex h-80 gap-4">
                <div className="w-24 flex-shrink-0 space-y-2 overflow-y-auto">
                  {thumbnails.map((image) => (
                    <img
                      key={image.id}
                      src={`https://app.bigdogtools.com/sites/image.php?v=v2&scale=0.3&dir=m&filename=${image.url}`}
                      alt={`Thumbnail ${image.id}`}
                      className="w-full h-20 object-cover rounded-md cursor-pointer hover:ring-2 hover:ring-orange-400 transition"
                      onClick={() => handleThumbnailClick(image)}
                    />
                  ))}
                </div>
                <div
                  className="flex-grow w-full overflow-hidden rounded-md cursor-pointer"
                  onClick={openImageModal}
                >
                  <img
                    src={`https://app.bigdogtools.com/sites/image.php?v=v2&scale=0.7&dir=m&filename=${mainImage.url}`}
                    alt="Main Display"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400">
                  <CalendarIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Lead Basics</h3>
              </div>
              <div className="space-y-2">
                <DetailRow
                  label="Lead Date"
                  isDate={true}
                  value={
                    lead.lead_date ? moment(lead.lead_date).format("LL") : "N/A"
                  }
                  fieldName="lead_date"
                  onClick={handleDetailClick}
                />
                <DetailRow
                  label="Lead Source"
                  value={lead.input_source}
                  fieldName="input_source"
                  onClick={handleDetailClick}
                />
                <DetailRow
                  label="Lead Name"
                  value={lead.property_lead_name}
                  fieldName="property_lead_name"
                  onClick={handleDetailClick}
                />
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400">
                  <MapPinIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Property Location</h3>
              </div>
              <div className="space-y-2">
                <DetailRow
                  label="Address"
                  value={lead.property_address}
                  fieldName="property_address"
                  onClick={handleDetailClick}
                />
                <DetailRow
                  label="City"
                  value={lead.property_city}
                  fieldName="property_city"
                  onClick={handleDetailClick}
                />
                <DetailRow
                  label="State"
                  value={lead.property_state}
                  fieldName="property_state"
                  onClick={handleDetailClick}
                />
                <DetailRow
                  label="Zip Code"
                  value={lead.property_zip}
                  fieldName="property_zip"
                  onClick={handleDetailClick}
                  isNumber={true}
                />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                  <UserIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Owner Information</h3>
              </div>
              <div className="space-y-2">
              <DetailRow
                label="Owner Name"
                value={lead.property_owner_name}
                fieldName="property_owner_name"
                onClick={handleDetailClick}
              />
              <DetailRow
                label="Secondary Contact"
                value={lead.secondary_contact}
                fieldName="secondary_contact"
                onClick={handleDetailClick}
                isTel={true}
              />
              <DetailRow
                label="Mailing Address"
                value={lead.property_address_mailing.replace(
                  "Possible Mailing Address: ",
                  "",
                )}
                fieldName="property_address_mailing"
                onClick={handleDetailClick}
              />
              <DetailRow
                label="Primary Number"
                value={formatMobileNumber(lead.mobile_number)}
                fieldName="mobile_number"
                onClick={handleDetailClick}
                isTel={true}
              />
              <DetailRow
                label="Alternate Number"
                value={formatMobileNumber(lead.alternate_number)}
                fieldName="alternate_number"
                onClick={handleDetailClick}
                isTel={true}
              />
                <DetailRow
                  label="Email Address"
                  value={lead.email_address}
                  fieldName="email_address"
                  onClick={handleDetailClick}
                />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                  <ClockIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Seller Situation</h3>
              </div>
              <div className="space-y-2">
                <DetailRow
                  label="Minimum Asking Price"
                  value={parseInt(lead.asking_price).toLocaleString("en-US")}
                  isNumber={true}
                  fieldName="asking_price"
                  onClick={handleDetailClick}
                />
                <DetailRow
                  label="Quickly Needs to Sell"
                  value={lead.need_to_sell_days}
                  fieldName="need_to_sell_days"
                  onClick={handleDetailClick}
                  isNumber={true}
                />
                <DetailRow
                  label="Needs Another Home"
                  value={lead.needs_home === "1" ? "Yes" : "No"}
                  fieldName="needs_home"
                  onClick={handleDetailClick}
                  isBool={true}
                />
                <DetailRow
                  label="MLS Listed"
                  value={lead.property_listed === "1" ? "Yes" : "No"}
                  fieldName="property_listed"
                  onClick={handleDetailClick}
                  isBool={true}
                />
                <DetailRow
                  label="Foreclosed"
                  value={lead.property_isForeclosed === "1" ? "Yes" : "No"}
                  fieldName="property_isForeclosed"
                  onClick={handleDetailClick}
                  isBool={true}
                />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                  <ShieldIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Escrow</h3>
              </div>
              <div className="space-y-2">
                <DetailRow
                  label="Insurance is Escrowed"
                  value={lead.insur_escrowed === "1" ? "Yes" : "No"}
                  fieldName="insur_escrowed"
                  onClick={handleDetailClick}
                  isBool={true}
                />
                <DetailRow
                  label="Tax is Escrowed"
                  value={lead.tax_escrowed === "1" ? "Yes" : "No"}
                  fieldName="tax_escrowed"
                  onClick={handleDetailClick}
                  isBool={true}
                />
                <DetailRow
                  label="HOA/PMI/Other is Escrowed"
                  value={lead.other_escrowed === "1" ? "Yes" : "No"}
                  fieldName="other_escrowed"
                  onClick={handleDetailClick}
                  isBool={true}
                />
              </div>
            </div>
          </div>

          {/* Middle Column */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                  <DollarIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Financial Overview</h3>
              </div>
              <div className="flex justify-around mb-6 py-4 border-b border-gray-100 dark:border-gray-700">
                <div
                  onClick={() => {
                    setActiveModal("price");
                  }}
                  className="flex flex-col items-center cursor-pointer hover:opacity-75 transition"
                >
                  <div className="text-gray-500 text-xs dark:text-gray-200 mb-1 font-semibold uppercase">Value</div>
                  <div className="font-bold text-lg text-emerald-600 dark:text-emerald-400">
                    ${ARV.toLocaleString()}
                  </div>
                </div>
                <div
                  onClick={() => {
                    setActiveModal("debt");
                  }}
                  className="flex flex-col items-center cursor-pointer hover:opacity-75 transition"
                >
                  <div className="text-gray-500 text-xs dark:text-gray-200 mb-1 font-semibold uppercase">Debt</div>
                  <div className="font-bold text-lg text-emerald-600 dark:text-emerald-400">
                    ${debt.toLocaleString()}
                  </div>
                </div>
                <div
                  onClick={() => {
                    setActiveModal("repairs");
                  }}
                  className="flex flex-col items-center cursor-pointer hover:opacity-75 transition"
                >
                  <div className="text-gray-500 text-xs dark:text-gray-200 mb-1 font-semibold uppercase">Repairs</div>
                  <div className="font-bold text-lg text-emerald-600 dark:text-emerald-400">
                    ${repairs.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="mb-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="p-2.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                    <ChartIcon />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white">Offer Analysis</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">Investment strategies ranked by profitability and feasibility</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 pt-3 mt-3 border-t border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500 shadow-sm"></div>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Viable</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500 shadow-sm"></div>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Caution</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500 shadow-sm"></div>
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Not Viable</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
              <StoplightLabel
                label="Wholesale"
                onClick={handleLabelClick}
                value={0.65 * ARV - repairs}
                className={getWholeSaleLabelClasses(EQP, listingIsNotGreen)}
              />
              <StoplightLabel
                label="Equity Partner"
                onClick={handleLabelClick}
                value={Math.max(debt, 0.5 * ARV - repairs)}
                className={getEquityPartnerLabelClasses(EQP, listingIsNotGreen)}
              />
              <StoplightLabel
                label="Short Sale"
                onClick={handleLabelClick}
                value={0.7 * ARV - repairs}
                className={getShortSaleLabelClasses(
                  EQP,
                  Lo1C,
                  listingIsNotGreen,
                )}
              />
              <StoplightLabel
                label="Fix & Flip"
                onClick={handleLabelClick}
                value={0.7 * ARV - repairs}
                className={getFixFlipLabelClasses(EQP, listingIsNotGreen)}
              />
              <StoplightLabel
                label="Flip on Wrap"
                onClick={handleLabelClick}
                value={0.75 * ARV - repairs}
                className={getFlipOnWrapLabelClasses(EQP, listingIsNotGreen)}
              />
              <StoplightLabel
                label="Mortgage Assn."
                onClick={handleLabelClick}
                value={debt}
                className={getMortAssignLabelClasses(
                  EQP,
                  Lo1C,
                  listingIsNotGreen,
                  ARV,
                  debt,
                  repairs,
                )}
              />
              <StoplightLabel
                label="Buy & Hold"
                onClick={handleLabelClick}
                value={0.85 * ARV - repairs}
                className={getBuyHoldLabelClasses(EQP, listingIsNotGreen)}
              />
              <StoplightLabel
                label="MLS"
                onClick={handleLabelClick}
                value={asisValue}
                className={getListingLabelClasses(
                  needsale,
                  listed,
                  valueNetProceedsRetail,
                )}
              />
              <StoplightLabel
                label="Lease Option"
                onClick={handleLabelClick}
                value={loyCal}
                className={getLeaseOptionLabelClasses(
                  state,
                  EQP,
                  Lo1C,
                  listingIsNotGreen,
                )}
              />
              <StoplightLabel
                label="Auction"
                value={0.85 * ARV - repairs}
                onClick={handleLabelClick}
                className={getAuctionOptionLabelClasses(EQP, listingIsNotGreen)}
              />
              <StoplightLabel
                label="House Swap"
                value={needHome === "1" ? "House" : "0"}
                className={getHouseSwapLabelClasses(
                  EQP,
                  needHome,
                  listingIsNotGreen,
                )}
              />
              <StoplightLabel
                label="Contract Deed"
                onClick={handleLabelClick}
                value={isCommercial ? (0.8 * ARV).toFixed(2) : "0"}
                className={getContractDeedLabelClasses(
                  isCommercial,
                  EQP,
                  listingIsNotGreen,
                )}
              />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                  <DollarIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Valuations</h3>
              </div>
              <div className="space-y-2">
                <DetailRow
                  label="CMA Estimate"
                value={`$${parseInt(
                  lead.property_cma_value || "0",
                ).toLocaleString()}`}
                fieldName="property_cma_value"
                onClick={handleDetailClick}
                isNumber={true}
              />
              <DetailRow
                label="Internet Estimate"
                value={`$${parseInt(lead.zestimate || "0").toLocaleString()}`}
                fieldName="zestimate"
                onClick={handleDetailClick}
                isNumber={true}
              />
              <DetailRow
                label="Investor Estimate"
                value={`$${parseInt(
                  lead.investor_price || "0",
                ).toLocaleString()}`}
                fieldName="investor_price"
                onClick={handleDetailClick}
                isNumber={true}
              />
              <DetailRow
                label="Tax Estimate"
                value={`$${parseInt(
                  lead.property_assdTtlValue || "0",
                ).toLocaleString()}`}
                fieldName="property_assdTtlValue"
                onClick={handleDetailClick}
                isNumber={true}
              />
              <DetailRow
                label="Seller Estimate"
                value={`$${parseInt(
                  lead.seller_wants_price || "0",
                ).toLocaleString()}`}
                fieldName="seller_wants_price"
                onClick={handleDetailClick}
                  isNumber={true}
                />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400">
                  <DocumentIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Property Records</h3>
              </div>
              <div className="space-y-2">
                <DetailRow
                  label="County"
                value={lead.subd_county}
                fieldName="subd_county"
                onClick={handleDetailClick}
              />
              <DetailRow
                label="Subdivision"
                value={lead.property_subdName}
                fieldName="property_subdName"
                onClick={handleDetailClick}
              />
              <DetailRow
                label="Legal Name"
                value={lead.property_legal_desc}
                fieldName="property_legal_desc"
                onClick={handleDetailClick}
              />
              <DetailRow
                label="Lot Sqft"
                value={parseInt(lead.property_lot_sqft || "0").toLocaleString(
                  "us-EN",
                )}
                fieldName="property_lot_sqft"
                onClick={handleDetailClick}
                isNumber={true}
              />
              <DetailRow
                label="Deed Date"
                isDate={true}
                value={
                  lead.property_saleRecDate === ""
                    ? ""
                    : moment(lead.property_saleRecDate).format("LL")
                }
                fieldName="property_saleRecDate"
                onClick={handleDetailClick}
              />
              <DetailRow
                label="Parcel ID"
                value={lead.property_parcel_id}
                fieldName="property_parcel_id"
                  onClick={handleDetailClick}
                />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                  <HomeIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Property Details</h3>
              </div>
              <div className="space-y-2">
                <DetailRow
                  label="Property Type"
                value={lead.property_type}
                fieldName="property_type"
                onClick={handleDetailClick}
              />
              <DetailRow
                label="Bedrooms"
                value={lead.property_beds}
                fieldName="property_beds"
                onClick={handleDetailClick}
                isNumber={true}
              />
              <DetailRow
                label="Bathrooms"
                value={lead.property_baths}
                fieldName="property_baths"
                onClick={handleDetailClick}
                isNumber={true}
              />
              <DetailRow
                label="Square Feet"
                value={lead.property_sqft}
                fieldName="property_sqft"
                onClick={handleDetailClick}
                isNumber={true}
              />
              <DetailRow
                label="Year Built"
                value={lead.property_year_built}
                fieldName="property_year_built"
                onClick={handleDetailClick}
                isNumber={true}
              />
              <DetailRow
                label="Property Sqft"
                value={parseInt(lead.property_sqft || "0").toLocaleString(
                  "us-EN",
                )}
                isNumber={true}
                fieldName="property_sqft"
                onClick={handleDetailClick}
              />
              <DetailRow
                label="Garage Number"
                value={lead.property_garage}
                fieldName="property_garage"
                onClick={handleDetailClick}
                isNumber={true}
              />
              <DetailRow
                label="Stories"
                value={lead.property_stories}
                fieldName="property_stories"
                onClick={handleDetailClick}
                isNumber={true}
              />
              <DetailRow
                label="Living Areas"
                value={lead.property_living_areas}
                fieldName="property_living_areas"
                onClick={handleDetailClick}
                isNumber={true}
              />
              <DetailRow
                label="Has Pool"
                value={lead.property_has_pool === "1" ? "Yes" : "No"}
                fieldName="property_has_pool"
                onClick={handleDetailClick}
                isBool={true}
              />
              <DetailRow
                label="Foundation Type"
                value={lead.property_foundation}
                fieldName="property_foundation"
                onClick={handleDetailClick}
              />
              <DetailRow
                label="Occupancy Status"
                value={lead.property_is_occupied}
                fieldName="property_is_occupied"
                onClick={handleDetailClick}
              />
              <DetailRow
                label="Property Condition"
                value={lead.property_condition}
                fieldName="property_condition"
                onClick={handleDetailClick}
              />
              <DetailRow
                label="Has HOA"
                value={lead.property_has_hoa === "1" ? "Yes" : "No"}
                fieldName="property_has_hoa"
                onClick={handleDetailClick}
                  isBool={true}
                />
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400">
                  <LinkIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Other Liens</h3>
              </div>
              <div className="space-y-2">
                <DetailRow
                  label="Lien Amounts"
                value={`$${parseInt(
                  lead.other_lien_amounts || "0",
                ).toLocaleString()}`}
                fieldName="other_lien_amounts"
                onClick={handleDetailClick}
                  isNumber={true}
                />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                  <CreditCardIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">First Mortgage</h3>
              </div>
              <div className="space-y-2">
                <DetailRow
                  label="Has First Mortgage"
                value={lead.bool_firstLoan === "1" ? "Yes" : "No"}
                fieldName="bool_firstLoan"
                onClick={handleDetailClick}
                isBool={true}
              />
              <DetailRow
                label="Origination Date"
                isDate={true}
                value={
                  lead.loan_1_origin
                    ? moment(lead.loan_1_origin).format("LL")
                    : "N/A"
                }
                fieldName="loan_1_origin"
                onClick={handleDetailClick}
              />
              <DetailRow
                label="Original Amount"
                value={`$${parseInt(
                  lead.loan_1_loan_amount || "0",
                ).toLocaleString()}`}
                fieldName="loan_1_loan_amount"
                onClick={handleDetailClick}
                isNumber={true}
              />
              <DetailRow
                label="Interest Rate"
                value={parseFloat(lead?.loan_1_loan_intrest_rate || 0).toFixed(3)}
                fieldName="loan_1_loan_intrest_rate"
                onClick={handleDetailClick}
                isNumber={false}
              />
              <DetailRow
                label="Balance"
                value={`$${parseInt(
                  lead.loan_1_loan_balance || "0",
                ).toLocaleString()}`}
                fieldName="loan_1_loan_balance"
                onClick={handleDetailClick}
                isNumber={true}
              />
              <DetailRow
                label="Term"
                value={`${lead.loan_1_loan_term} years`}
                fieldName="loan_1_loan_term"
                onClick={handleDetailClick}
                isNumber={true}
              />
              <DetailRow
                label="Type"
                value={lead.loan_1_loan_type}
                fieldName="loan_1_loan_type"
                onClick={handleDetailClick}
              />
              <DetailRow
                label="Last Pay Date"
                isDate={true}
                value={
                  lead.loan_1_last_pay === ""
                    ? ""
                    : moment(lead.loan_1_last_pay).format("LL")
                }
                fieldName="loan_1_last_pay"
                onClick={handleDetailClick}
              />
              <DetailRow
                label="Back Pay Amount"
                value={`$${parseInt(
                  lead.loan_1_back_pay || "0",
                ).toLocaleString()}`}
                fieldName="loan_1_back_pay"
                onClick={handleDetailClick}
                isNumber={true}
              />
              <DetailRow
                label="Lender Name"
                value={lead.loan_1_lender}
                fieldName="loan_1_lender"
                onClick={handleDetailClick}
              />
              <DetailRow
                label="Loan ID"
                value={lead.loan_1_loan_id}
                fieldName="loan_1_loan_id"
                  onClick={handleDetailClick}
                />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400">
                  <CreditCardIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Second Mortgage</h3>
              </div>
              <div className="space-y-2">
                <DetailRow
                  label="Has Second Mortgage"
                value={lead.bool_secondLoan === "1" ? "Yes" : "No"}
                fieldName="bool_secondLoan"
                onClick={handleDetailClick}
                isBool={true}
              />
              <DetailRow
                label="Origination Date"
                isDate={true}
                value={
                  lead.loan_2_origin
                    ? moment(lead.loan_2_origin).format("LL")
                    : "N/A"
                }
                fieldName="loan_2_origin"
                onClick={handleDetailClick}
              />
              <DetailRow
                label="Original Amount"
                value={`$${parseInt(
                  lead.loan_2_loan_amount || "0",
                ).toLocaleString()}`}
                fieldName="loan_2_loan_amount"
                onClick={handleDetailClick}
                isNumber={true}
              />
              <DetailRow
                label="Interest Rate"
                value={parseFloat(lead?.loan_2_loan_intrest_rate || 0).toFixed(3)}
                fieldName="loan_2_loan_intrest_rate"
                onClick={handleDetailClick}
                isNumber={false}
              />
              <DetailRow
                label="Balance"
                value={`$${parseInt(
                  lead.loan_2_loan_balance || "0",
                ).toLocaleString()}`}
                fieldName="loan_2_loan_balance"
                onClick={handleDetailClick}
                isNumber={true}
              />
              <DetailRow
                label="Term"
                value={`${lead.loan_2_loan_term} years`}
                fieldName="loan_2_loan_term"
                onClick={handleDetailClick}
                isNumber={true}
              />
              <DetailRow
                label="Type"
                value={lead.loan_2_loan_type}
                fieldName="loan_2_loan_type"
                onClick={handleDetailClick}
              />
              <DetailRow
                label="Last Pay Date"
                isDate={true}
                value={
                  lead.loan_2_last_pay === ""
                    ? ""
                    : moment(lead.loan_1_last_pay).format("LL")
                }
                fieldName="loan_1_last_pay"
                onClick={handleDetailClick}
              />
              <DetailRow
                label="Back Pay Amount"
                value={`$${parseInt(
                  lead.loan_2_back_pay || "0",
                ).toLocaleString()}`}
                fieldName="loan_2_back_pay"
                onClick={handleDetailClick}
                isNumber={true}
              />
              <DetailRow
                label="Lender Name"
                value={lead.loan_2_lender}
                fieldName="loan_2_lender"
                onClick={handleDetailClick}
              />
              <DetailRow
                label="Loan ID"
                value={lead.loan_2_loan_id}
                fieldName="loan_2_loan_id"
                  onClick={handleDetailClick}
                />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-900/30 text-slate-600 dark:text-slate-400">
                  <PenIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Lead Notes</h3>
              </div>
              <div
                className="w-full p-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 cursor-pointer text-gray-900 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 transition"
                onClick={() =>
                  handleDetailClick({
                    label: "Notes",
                    value: lead.notes,
                    fieldName: "notes",
                    isTextArea: true,
                  })
                }
              >
                <textarea
                  readOnly
                  className="w-full h-32 bg-transparent border-none resize-none dark:text-gray-200 text-sm"
                  value={lead.notes || ""}
                />
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-700">
                <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-900/30 text-gray-600 dark:text-gray-400">
                  <DocumentIcon />
                </div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100">Skiptrace Report</h3>
              </div>
              <textarea
                readOnly
                className="w-full h-40 p-3 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 uppercase text-xs font-medium text-gray-900 dark:text-gray-200"
                value={lead.skip_trace || ""}
              />
            </div>
          </div>
        </div>
      </div>
      <Modal
        show={helpOpen}
        onClose={() => {
          sethelpOpen(false);
        }}
        title="How to Edit Lead Data"
        maxWidth="max-w-lg"
        icon={
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        }
        iconColor="blue"
      >
        <div className="space-y-4 text-gray-700 dark:text-gray-200">
          <p>
            You can easily view and edit lead details directly from this list.
          </p>
          <ol className="list-decimal list-inside space-y-2 pl-4">
            <li>
              <strong>Click on any row</strong> in the table below to open the
              "Lead Details" modal.
            </li>
            <li>Inside the modal, edit the field with your desired data.</li>
            <li>
              Once you are finished, click <strong>"Save Changes"</strong> to
              update the record.
            </li>
          </ol>
        </div>
      </Modal>
      <DebtCalculatorModal
        isOpen={isModalMCOpen}
        onClose={() => setIsModalMCOpen(false)}
        initialData={{ leads }}
      />
      <PriceValueModal
        visible={activeModal === "price"}
        onClose={() => setActiveModal(null)}
        lead={leads}
        user={user}
        setError={setError}
        pullLeads={pullLeads}
      />
      <SellerDebtModal
        visible={activeModal === "debt"}
        onClose={() => setActiveModal(null)}
        lead={leads}
        user={user}
        setError={setError}
        pullLeads={pullLeads}
      />
      <RepairsModal
        visible={activeModal === "repairs"}
        onClose={() => setActiveModal(null)}
        lead={leads}
        user={user}
        setError={setError}
        pullLeads={pullLeads}
      />

      <PhotoManagerModal
        isOpen={modalImage}
        onClose={() => setModalImage(false)}
        existingPhotos={thumbnails}
        user={user}
        pid={leads.pid}
        pullData={pullAllImages}
        pullLeads={pullLeads}
        setError={setError}
      />
      <Modal
        show={isAiConfirmOpen}
        onClose={() => setIsAiConfirmOpen(false)}
        title="Confirm AI Query"
        icon={
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
            <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
          </svg>
        }
        iconColor="indigo"
        footer={
          <div className="flex gap-4">
            <button
              onClick={() => handleAction("ai")}
              disabled={loading.ai}
              className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700"
            >
              {loading.ai ? "Querying..." : "Confirm"}
            </button>
          </div>
        }
      >
        <p>
          This will use 1 AI Query Credit to populate data for this lead. Are
          you sure you want to continue?
        </p>
      </Modal>

      <Modal
        show={isSkipConfirmOpen}
        onClose={() => setIsSkipConfirmOpen(false)}
        title="Confirm Skiptrace"
        icon={
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
          </svg>
        }
        iconColor="purple"
        footer={
          <div className="flex gap-4">
            <button
              onClick={() => handleAction("skip")}
              disabled={loading.skip}
              className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700"
            >
              {loading.skip ? "Skiptracing..." : "Skiptrace Primary"}
            </button>
            <button
              onClick={() => handleAction("skip2")}
              disabled={loading.skip2}
              className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-orange-600 hover:bg-orange-700 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700"
            >
              {loading.skip2 ? "Skiptracing..." : "Skiptrace Secondary"}
            </button>
          </div>
        }
      >
        <p>
          This will use 1 Skiptrace Credit to find information for the lead(s).
          Are you sure you want to continue?
        </p>
      </Modal>
      <Modal
        show={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        title="Confirm Deletion"
        icon={
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        }
        iconColor="red"
        footer={
          <div className="flex gap-4">
            <button
              onClick={() => handleAction("delete")}
              disabled={loading.delete}
              className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-900 dark:border dark:border-gray-700"
            >
              {loading.delete ? "Deleting..." : "Confirm Delete"}
            </button>
          </div>
        }
      >
        <p>
          Are you sure you want to permanently delete this lead? This action
          cannot be undone.
        </p>
      </Modal>
      <AutoresponderModal
        isOpen={isAutoresponderModalOpen}
        onClose={() => setIsAutoresponderModalOpen(false)}
        lead={lead}
        user={user}
        onSaveSuccess={onSaveSuccess}
        autoresponders={sellerAR}
      />
      <DescriptionModal
        isOpen={isDescriptionModalOpen}
        onClose={() => setIsDescriptionModalOpen(false)}
        title={modalContent.title}
        description={modalContent.description}
      />
      <EditDetailModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        fieldData={fieldToEdit}
        onSave={onSaveSuccess}
        user={user}
        setError={setError}
        pullLeads={pullLeads}
      />
      {isImageModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          onClick={() => setIsImageModalOpen(false)}
        >
          <img
            src={`https://app.bigdogtools.com/sites/image.php?v=v2&scale=1.0&dir=m&filename=${mainImage.url}`}
            alt="Full Screen"
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}
    </PageContent>
  );
};

export default EditLeadPage;
